import api from './api';

/**
 * Servicio para gestionar las operaciones de secciones
 */
export const seccionesService = {
  /**
   * Obtiene todas las secciones ordenadas por código
   * @param {Object} options - Opciones de consulta
   * @param {number} options.page - Número de página (default: 0)
   * @param {string} options.sortBy - Campo de ordenamiento (default: 'Codigo')
   * @param {string} options.sort - Dirección del ordenamiento (default: 'asc')
   * @returns {Promise<Object>} Respuesta de la API
   */
  async findAll(options = {}) {
    const params = {
      page: options.page || 0,
      sortBy: options.sortBy || 'Codigo',
      sort: options.sort || 'asc',
    };

    try {
      const result = await api.get('/secciones', { params });
      return {
        success: true,
        data: result.data,
        message: 'Secciones obtenidas correctamente',
      };
    } catch (error) {
      return {
        success: false,
        data: null,
        message: error.message || 'Error al obtener las secciones',
      };
    }
  },

  /**
   * Obtiene secciones de forma paginada
   * @param {number} page - Número de página
   * @param {Object} options - Opciones adicionales
   * @returns {Promise<Object>} Respuesta de la API
   */
  async findPaginated(page = 1, options = {}) {
    return this.findAll({
      page,
      sortBy: options.sortBy || 'Codigo',
      sort: options.sort || 'asc',
    });
  },

  /**
   * Obtiene una sección por su ID
   * @param {number} id - ID de la sección
   * @returns {Promise<Object>} Respuesta de la API
   */
  async findById(id) {
    try {
      const result = await api.get(`/secciones/${id}`);
      return {
        success: true,
        data: result.data,
        message: 'Sección obtenida correctamente',
      };
    } catch (error) {
      return {
        success: false,
        data: null,
        message: error.message || 'Error al obtener la sección',
      };
    }
  },

  /**
   * Crea una nueva sección
   * @param {Object} seccionData - Datos de la sección
   * @returns {Promise<Object>} Respuesta de la API
   */
  async create(seccionData) {
    try {
      const result = await api.post('/secciones', seccionData);
      return {
        success: true,
        data: result.data,
        message: 'Sección creada correctamente',
      };
    } catch (error) {
      return {
        success: false,
        data: null,
        message: error.message || 'Error al crear la sección',
      };
    }
  },

  /**
   * Actualiza una sección existente
   * @param {number} id - ID de la sección
   * @param {Object} seccionData - Datos actualizados de la sección
   * @returns {Promise<Object>} Respuesta de la API
   */
  async update(id, seccionData) {
    try {
      const result = await api.put(`/secciones/${id}`, seccionData);
      return {
        success: true,
        data: result.data,
        message: 'Sección actualizada correctamente',
      };
    } catch (error) {
      return {
        success: false,
        data: null,
        message: error.message || 'Error al actualizar la sección',
      };
    }
  },

  /**
   * Elimina una sección
   * @param {number} id - ID de la sección
   * @returns {Promise<Object>} Respuesta de la API
   */
  async delete(id) {
    try {
      const result = await api.delete(`/secciones/${id}`);
      return {
        success: true,
        data: result.data,
        message: 'Sección eliminada correctamente',
      };
    } catch (error) {
      return {
        success: false,
        data: null,
        message: error.message || 'Error al eliminar la sección',
      };
    }
  },

  /**
   * Busca secciones por criterios específicos
   * @param {Object} searchCriteria - Criterios de búsqueda
   * @returns {Promise<Object>} Respuesta de la API
   */
  async search(searchCriteria) {
    try {
      const result = await api.get('/secciones/search', { params: searchCriteria });
      return {
        success: true,
        data: result.data,
        message: 'Búsqueda completada correctamente',
      };
    } catch (error) {
      return {
        success: false,
        data: null,
        message: error.message || 'Error en la búsqueda de secciones',
      };
    }
  },
};

export default seccionesService;
